import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { StatusResult } from '../../types';
import { RISK_LEVELS } from '@immigration-helper/shared';

interface ResultsDisplayProps {
  result: StatusResult;
  onStartOver: () => void;
}

export default function ResultsDisplay({ result, onStartOver }: ResultsDisplayProps) {
  const { t } = useTranslation();
  const riskInfo = RISK_LEVELS[result.primaryStatus.riskLevel as keyof typeof RISK_LEVELS];

  const confidenceColors = {
    high: 'text-green-600 bg-green-50',
    medium: 'text-amber-600 bg-amber-50',
    low: 'text-red-600 bg-red-50',
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t('results.title')}
        </h1>
        <p className="text-gray-600">
          {t('results.likelyStatus')}
        </p>
      </div>

      {/* Status Card */}
      <div className="card mb-8">
        <div className="flex items-start gap-4">
          {/* Status Icon */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${riskInfo.color}20` }}
          >
            <svg
              className="w-6 h-6"
              style={{ color: riskInfo.color }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {result.primaryStatus.riskLevel === 'lawful' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : result.primaryStatus.riskLevel === 'protected' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              )}
            </svg>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-gray-900">
                {result.primaryStatus.name}
              </h2>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${confidenceColors[result.confidence]}`}
              >
                {t(`results.confidence.${result.confidence}`)}
              </span>
            </div>
            <p className="text-gray-600">
              {result.primaryStatus.description}
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-amber-800">
            {t('results.disclaimer')}
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="card mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('results.nextSteps')}
        </h3>
        <ul className="space-y-3">
          {result.recommendedActions.map((action, index) => (
            <li key={index} className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                {index + 1}
              </span>
              <span className="text-gray-700">{action}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Legal Organizations */}
      {result.matchedOrganizations.length > 0 && (
        <div className="card mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('results.findHelp')}
          </h3>
          <div className="space-y-4">
            {result.matchedOrganizations.slice(0, 3).map(org => (
              <div key={org.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{org.name}</h4>
                  {org.city && org.state && (
                    <p className="text-sm text-gray-500">{org.city}, {org.state}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    {org.isFree && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                        Free services
                      </span>
                    )}
                    {org.isVerified && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
                {org.phone && (
                  <a
                    href={`tel:${org.phone}`}
                    className="btn-secondary text-sm py-2"
                  >
                    Call
                  </a>
                )}
              </div>
            ))}
          </div>
          <Link
            to="/resources"
            className="block text-center text-primary-600 hover:text-primary-700 font-medium mt-4"
          >
            View all resources →
          </Link>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={onStartOver}
          className="btn-secondary flex-1"
        >
          {t('results.startOver')}
        </button>
        <button
          onClick={() => window.print()}
          className="btn-primary flex-1"
        >
          {t('results.print')}
        </button>
      </div>
    </div>
  );
}
